/**
 * Browser-side speech service. Owns one Deepgram live WebSocket per source.
 * The renderer's existing `useAudioStream` hook (mic branch) keeps working
 * unchanged because it calls `window.app.speech.{start,stop,pushAudio}` —
 * we just route those into a browser-native client instead of IPC.
 *
 * System-source audio capture is impossible on mobile browsers, so
 * `start('system')` throws. The PWA shell never exposes the toggle.
 *
 * Reconnect rules mirror `src/main/speech.ts`:
 *  - User-initiated stop → no reconnect.
 *  - Initial connect that never reached Open → no reconnect (auth failure).
 *  - Otherwise: exponential backoff (1s → 30s cap), retry indefinitely until
 *    stop() is called. Lets a Wi-Fi drop / sleep recover automatically.
 *
 * NOTE: STT engine selection in PWA Settings (Deepgram vs AssemblyAI)
 * currently only persists the user's preference + key. The runtime mic
 * stream still routes through Deepgram regardless. Wiring AssemblyAI's
 * Universal-Streaming v3 WebSocket client (~250 LOC mirroring the
 * desktop's `src/main/stt/assemblyai.ts`) is a planned follow-up — the
 * Settings UI lets the user save the key now so the eventual switch is
 * a one-line change here.
 */
import { createClient, LiveTranscriptionEvents, type LiveClient } from '@deepgram/sdk';
import type {
  AudioSource,
  SpeechErrorEvent,
  SpeechLevelEvent,
  SpeechStatusEvent,
  SpeechTranscriptEvent,
  SpeechUtteranceEvent,
} from '../../shared/ipc';
import { keys } from './keystore';

const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;

interface ActiveSession {
  live: LiveClient;
  keepAlive: number;
  stopping: boolean;
  everOpened: boolean;
  reconnectAttempt: number;
  reconnectTimer: number | null;
}

type Listeners = {
  transcript: Set<(e: SpeechTranscriptEvent) => void>;
  status: Set<(e: SpeechStatusEvent) => void>;
  error: Set<(e: SpeechErrorEvent) => void>;
  level: Set<(e: SpeechLevelEvent) => void>;
  utterance: Set<(e: SpeechUtteranceEvent) => void>;
};
const listeners: Listeners = {
  transcript: new Set(),
  status: new Set(),
  error: new Set(),
  level: new Set(),
  utterance: new Set(),
};
const sessions = new Map<AudioSource, ActiveSession>();

function emitStatus(e: SpeechStatusEvent): void {
  for (const l of listeners.status) l(e);
}
function emitError(e: SpeechErrorEvent): void {
  for (const l of listeners.error) l(e);
}
function emitTranscript(e: SpeechTranscriptEvent): void {
  for (const l of listeners.transcript) l(e);
}
function emitLevel(e: SpeechLevelEvent): void {
  for (const l of listeners.level) l(e);
}

const lastEmittedAt: Record<AudioSource, number> = { microphone: 0, system: 0 };
function rmsFromInt16(buf: ArrayBuffer): number {
  const view = new Int16Array(buf);
  if (view.length === 0) return 0;
  let sumSq = 0;
  for (let i = 0; i < view.length; i++) {
    const s = view[i] / 32768;
    sumSq += s * s;
  }
  return Math.sqrt(sumSq / view.length);
}
function maybeEmitLevel(source: AudioSource, chunk: ArrayBuffer): void {
  const now = Date.now();
  if (now - lastEmittedAt[source] < 33) return;
  lastEmittedAt[source] = now;
  emitLevel({ source, level: rmsFromInt16(chunk) });
}

async function makeSession(source: AudioSource): Promise<void> {
  const apiKey = keys.getDeepgram();
  if (!apiKey) {
    emitError({ source, message: 'Deepgram API key missing — paste it in Settings.' });
    emitStatus({ source, status: 'error' });
    sessions.delete(source);
    return;
  }

  emitStatus({ source, status: 'connecting' });

  const dg = createClient(apiKey);
  const live = dg.listen.live({
    model: 'nova-3',
    language: 'en-US',
    encoding: 'linear16',
    sample_rate: 16000,
    channels: 1,
    interim_results: true,
    smart_format: true,
    punctuate: true,
    endpointing: 300,
    vad_events: true,
  });

  const keepAlive = window.setInterval(() => {
    live.keepAlive();
  }, 8000);

  const existing = sessions.get(source);
  const session: ActiveSession = existing
    ? Object.assign(existing, { live, keepAlive, reconnectTimer: null })
    : {
        live,
        keepAlive,
        stopping: false,
        everOpened: false,
        reconnectAttempt: 0,
        reconnectTimer: null,
      };
  sessions.set(source, session);

  live.on(LiveTranscriptionEvents.Open, () => {
    const s = sessions.get(source);
    if (s) {
      s.everOpened = true;
      s.reconnectAttempt = 0;
    }
    emitStatus({ source, status: 'open' });
  });

  live.on(LiveTranscriptionEvents.Transcript, (data) => {
    const alt = data.channel?.alternatives?.[0];
    const text = alt?.transcript ?? '';
    if (!text) return;
    emitTranscript({ source, text, isFinal: Boolean(data.is_final) });
  });

  live.on(LiveTranscriptionEvents.Error, (err) => {
    const s = sessions.get(source);
    const raw = (err as Error)?.message ?? String(err);
    const friendly =
      /Ready State: CLOSED/i.test(raw) && !s?.everOpened
        ? 'Deepgram rejected the connection — your API key is likely invalid or expired. Open Settings → API & Model and re-paste it.'
        : raw;
    emitError({ source, message: friendly });
  });

  live.on(LiveTranscriptionEvents.Close, () => {
    const s = sessions.get(source);
    if (!s) {
      emitStatus({ source, status: 'closed' });
      return;
    }
    window.clearInterval(s.keepAlive);

    if (s.stopping) {
      emitStatus({ source, status: 'closed' });
      return;
    }

    if (!s.everOpened) {
      emitStatus({ source, status: 'closed' });
      sessions.delete(source);
      return;
    }

    s.reconnectAttempt += 1;
    const delay = Math.min(
      MAX_RECONNECT_DELAY_MS,
      BASE_RECONNECT_DELAY_MS * 2 ** (s.reconnectAttempt - 1),
    );
    emitStatus({ source, status: 'connecting' });
    s.reconnectTimer = window.setTimeout(() => {
      const cur = sessions.get(source);
      if (!cur || cur.stopping) return;
      cur.reconnectTimer = null;
      void makeSession(source);
    }, delay);
  });
}

function safeSend(session: ActiveSession, chunk: ArrayBuffer): void {
  try {
    session.live.send(chunk);
  } catch {
    // live client mid-reconnect — drop chunk
  }
}

export const speech = {
  async start(source: AudioSource): Promise<void> {
    if (source === 'system') {
      throw new Error(
        'System audio capture is not supported in the mobile PWA. Use the desktop app for system audio.',
      );
    }
    if (sessions.has(source)) return;
    await makeSession(source);
  },

  async stop(source: AudioSource): Promise<void> {
    const session = sessions.get(source);
    if (!session) return;
    session.stopping = true;
    if (session.reconnectTimer != null) {
      window.clearTimeout(session.reconnectTimer);
      session.reconnectTimer = null;
    }
    window.clearInterval(session.keepAlive);
    try {
      session.live.requestClose();
    } catch {
      // already closing
    }
    sessions.delete(source);
  },

  pushAudio(source: AudioSource, chunk: ArrayBuffer): void {
    const session = sessions.get(source);
    if (!session) return;
    safeSend(session, chunk);
    maybeEmitLevel(source, chunk);
  },

  async isNativeSystemAvailable(): Promise<boolean> {
    return false;
  },

  onTranscript(handler: (e: SpeechTranscriptEvent) => void): () => void {
    listeners.transcript.add(handler);
    return () => {
      listeners.transcript.delete(handler);
    };
  },
  onStatus(handler: (e: SpeechStatusEvent) => void): () => void {
    listeners.status.add(handler);
    return () => {
      listeners.status.delete(handler);
    };
  },
  onError(handler: (e: SpeechErrorEvent) => void): () => void {
    listeners.error.add(handler);
    return () => {
      listeners.error.delete(handler);
    };
  },
  onLevel(handler: (e: SpeechLevelEvent) => void): () => void {
    listeners.level.add(handler);
    return () => {
      listeners.level.delete(handler);
    };
  },
  /**
   * Mirrors the desktop's `onUtterance` event. Currently the PWA's
   * Deepgram client doesn't emit one — the mobile shell auto-asks off
   * `onTranscript`'s `is_final` chunks today (which is the same regex
   * heuristic the desktop just moved away from). When we port the
   * UtteranceEnd flow to the PWA, this handler set will start firing.
   * For now it stays a no-op subscription so shared modules can register
   * listeners without crashing.
   */
  onUtterance(handler: (e: SpeechUtteranceEvent) => void): () => void {
    listeners.utterance.add(handler);
    return () => {
      listeners.utterance.delete(handler);
    };
  },
  /**
   * No-op stub — desktop-only feature. The PWA's speech client doesn't
   * buffer utterances server-side, so force-send paths in shared code
   * resolve cleanly without doing anything here.
   */
  flushUtterance(_source: AudioSource): void {
    /* intentionally empty */
  },
  /** No-op stub — PWA Deepgram session doesn't yet expose utterance-end
   *  config. Shared code can call this safely. */
  setUtteranceEndMs(_ms: number): void {
    /* intentionally empty */
  },
  /** No-op stub — PWA only supports Deepgram for STT today. The desktop
   *  exposes a real `setEngine` IPC that hot-swaps engines; the PWA
   *  ignores it so shared code can call it without crashing. */
  setEngine(_engineId: 'deepgram' | 'assemblyai'): void {
    /* intentionally empty */
  },
};

export async function testDeepgramKey(plaintext: string): Promise<void> {
  const key = plaintext.trim() || keys.getDeepgram() || '';
  if (!key) throw new Error('No Deepgram API key set. Paste one or save it in Settings first.');
  const dg = createClient(key);
  const live = dg.listen.live({
    model: 'nova-3',
    encoding: 'linear16',
    sample_rate: 16000,
    channels: 1,
  });
  return new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      try { live.requestClose(); } catch { /* ignore */ }
      reject(
        new Error(
          'Deepgram timed out (5s). The browser console likely shows "WebSocket connection failed" — check your key in console.deepgram.com → API Keys. ' +
            'Note: some Deepgram accounts block long-lived API keys from browser origins; if your desktop app works with the same key, the PWA may need a server-issued temp token.',
        ),
      );
    }, 5000);
    live.on(LiveTranscriptionEvents.Open, () => {
      window.clearTimeout(timer);
      try { live.requestClose(); } catch { /* ignore */ }
      resolve();
    });
    live.on(LiveTranscriptionEvents.Error, (err) => {
      window.clearTimeout(timer);
      try { live.requestClose(); } catch { /* ignore */ }
      reject(err instanceof Error ? err : new Error(String(err)));
    });
    // Some browsers (and the @deepgram/sdk in browser mode) surface
    // handshake-time rejections via the Close event, not Error. Capture
    // the close code + reason so the user sees something more useful
    // than "WebSocket connection failed".
    live.on(LiveTranscriptionEvents.Close, (evt: { code?: number; reason?: string }) => {
      window.clearTimeout(timer);
      // Open path already cleared the timer; reject only if we never opened.
      const code = evt?.code;
      const reason = evt?.reason || '';
      const hints: Record<number, string> = {
        1006: 'connection closed abnormally — most often an invalid API key or a Deepgram-blocked origin',
        1008: 'policy violation — Deepgram rejected the key or the request payload',
        4001: 'unauthorized — the key is invalid, revoked, or missing the required scope',
        4003: 'forbidden — your account may not have access to the nova-3 model',
        4008: 'project rate-limited',
      };
      const hint = code !== undefined ? hints[code] : undefined;
      reject(
        new Error(
          `Deepgram closed the connection (code ${code ?? '?'}${reason ? ` — ${reason}` : ''})${
            hint ? `. ${hint}` : ''
          }. Verify the key at console.deepgram.com.`,
        ),
      );
    });
  });
}
